import React, {useContext, useEffect, useState} from "react";
import "./LandingPage.css";
import {Button, GlobalMenu, TextArea} from "@spg/component_library";
import {Applications, User} from "./utility/Types";
import API, {Resolution} from "../logic/API";
import {useIntl} from "react-intl";
import {NavLink} from "react-router-dom";
import {KeycloakContext} from "./utility/KeycloakProvider";
import {Data} from "../logic/APITypes";
import {AppSettingsContext} from "./utility/AppSettingsProvider";

/**
 * A view fetching and displaying the most recent data objects with a button to request an updated version.
 */
function LandingPage() {
  const {keycloak, initialized} = useContext(KeycloakContext);
  const {portalURL} = useContext(AppSettingsContext);
  const intl = useIntl();
  const [userInfo, setUserInfo] = useState<User>({
    firstName: "–",
    lastName: " ",
    email: "–",
  });
  const [applications, setApplications] = useState<Resolution<Applications>>({
    success: false,
    unresolved: true,
  });
  const [data, setData] = useState<Data>("");
  const [dataResolution, setDataResolution] = useState<Resolution<Data>>({
    success: false,
    unresolved: true,
  });

  useEffect(() => {
    if (keycloak.idTokenParsed)
      setUserInfo({
        firstName: keycloak.idTokenParsed["given_name"],
        lastName: keycloak.idTokenParsed["family_name"],
        email: keycloak.idTokenParsed["email"],
      });
  }, [initialized]);

  const loadApps = async () => {
    setApplications(await API.getApplications());
  };

  const loadData = async () => {
    setDataResolution({
      success: false,
      unresolved: true,
    });
    const res = await API.getData();
    setDataResolution(res);
    if (res.payload) setData(res.payload);
  };

  useEffect(() => {
    loadApps();
    loadData();
  }, []);

  return (
    <div className="landingPageContainer">
      <div className="landingPageGlobalMenu">
        <GlobalMenu
          isLoggedIn
          firstName={userInfo.firstName}
          lastName={userInfo.lastName}
          email={userInfo.email}
          applications={applications.payload}
          altTextApplications={intl.formatMessage({
            id: "reusables.globalMenu.applications",
          })}
          emptyMessage={intl.formatMessage({
            id: applications.unresolved
              ? "reusables.globalMenu.applications.loading"
              : "reusables.globalMenu.applications.empty",
          })}
          ariaLabelUserImage={intl.formatMessage({
            id: "reusables.globalMenu.userWidget",
          })}
          primaryButton={
            <NavLink to={portalURL + "/account"} tabIndex={-1}>
              <Button
                label={intl.formatMessage({
                  id: "reusables.globalMenu.userWidget.management",
                })}
                id={"landingPageGlobalMenuAccountManagementButton"}
                onClick={() => null}
                configuration={"primary"}
              />
            </NavLink>
          }
          secondaryButton={
            <NavLink
              to={keycloak.createLogoutUrl({
                redirectUri: window.location.origin + "/login",
              })}
              className="landingPageGlobalMenuNavLink"
            >
              {intl.formatMessage({
                id: "reusables.globalMenu.userWidget.logout",
              })}
            </NavLink>
          }
          toggleNameTooltipMessage={intl.formatMessage({
            id: "reusables.globalMenu.userWidget.toggleName",
          })}
          toggleEmailTooltipMessage={intl.formatMessage({
            id: "reusables.globalMenu.userWidget.toggleMail",
          })}
          portalOrigin={portalURL}
          imprintLabel={intl.formatMessage({
            id: "reusables.globalMenu.userWidget.imprint",
          })}
          privacyPolicyLabel={intl.formatMessage({
            id: "reusables.globalMenu.userWidget.privacy",
          })}
        />
      </div>
      <main id="main" className="landingPageContent">
        <div className="landingPageGrid typographyTypoParagraphAllDevicesP landingPageError">
          <Button
            label={intl.formatMessage({id: "landingPage.reload"})}
            id={"landingPageReloadButton"}
            onClick={() => {
              loadData();
            }}
            configuration={"primary"}
            disabled={dataResolution.unresolved}
          />
          <span className="landingPageStatus">
            {dataResolution.unresolved
              ? intl.formatMessage({id: "landingPage.fetching"})
              : dataResolution.success
                ? intl.formatMessage({id: "landingPage.success"})
                : dataResolution.errorReason &&
                    typeof dataResolution.errorReason !== "string" &&
                    dataResolution.errorReason.length
                  ? `${dataResolution.httpCode} – ${dataResolution.errorReason[0].msg}`
                  : `${dataResolution.httpCode} – ${dataResolution.errorReason}`}
          </span>
          <TextArea
            label={intl.formatMessage({id: "landingPage.data"})}
            id={"landingPageDataTextArea"}
            value={data}
            onChange={() => null}
            readOnly
            isResizeable
            rows={30}
          />
        </div>
      </main>
    </div>
  );
}

export {LandingPage};
